import { useCallback, useMemo, useRef } from 'react';
import IOfferCardProp from './IOfferCardProp';
import './offer-card.scss';
import LoadCardHeader from
    '../../../../../common/components/load-card/load-card-header/load-card-header';
import { getCardCursor } from
    '../../../../../common/components/load-card/load-card-body/utils/load-card-body-utils';
import LoadCardBody from
    '../../../../../common/components/load-card/load-card-body/load-card-body';
import { IRouteDetails } from
    '../../../../../common/components/load-card/load-card-body/ILoadCardBody';
import {
    getAcceptedRateByVehicle,
    default as getOfferTagStatusType,
} from '../../utils/offers-utils';
import LoadCardFooter from
    '../../../../../common/components/load-card/load-card-footer/load-card-footer';
import { formatCurrency } from '../../../../../utils/currency-utils';
import OfferCardFooterButtons from '../offer-card-footer-buttons/offer-card-footer-buttons';
import LanguageContext from '../../../../../app/contexts/language';
import { getUTCDate } from '../../../../../utils/date-utils';
import { useOffersRound } from
    '../../../../../app/contexts/offers-round/offers-round.context';
import AuthContext from '../../../../../app/contexts/auth';

const { useAuth } = AuthContext;

export default function OfferCard({
    offer,
    isConfirmationEnabled = true,
}: Readonly<IOfferCardProp>) {
    const checkBoxRef = useRef<HTMLInputElement>(null);
    const { language } = LanguageContext.useLanguage();
    const {
        roundState,
        selectedOffersCount,
        selectOffer,
        acceptedOffersRates,
    } = useOffersRound();
    const { user } = useAuth();
    const acceptedRate = offer.accepted
        ? acceptedOffersRates[offer.loadOfferId] ??
          getAcceptedRateByVehicle(offer)
        : undefined;

    const confirmationStyleClass = useMemo(() => {
        if (!offer.confirmedBy) {
            return '';
        }
        const isConfirmedBySelf =
            offer.confirmedBy === user?.carrier?.sulahub_id;

        return isConfirmedBySelf
            ? 'offer-card--confirmed-self'
            : 'offer-card--confirmed-other';
    }, [offer.confirmedBy, user?.carrier?.sulahub_id]);

    const handleCardClick = useCallback(
        (event: any) => {
            const { classList } = event.target;

            if (
                selectedOffersCount < 1 ||
                !checkBoxRef.current ||
                classList.contains('checkmark') ||
                classList.contains('checkbox') ||
                classList.contains('button')
            )
                return;

            checkBoxRef.current.click();
        },
        [selectedOffersCount],
    );

    const midRoundBorder = offer.insertedMidRound
        ? 'offer-mid-round-border'
        : '';

    return (
        <div
            role="none"
            key={offer.loadOfferId}
            data-testid="offer-card"
            onClick={handleCardClick}
            className={`offer-card ${
                offer.checked && 'offer-card-selected'
            } ${midRoundBorder} ${confirmationStyleClass}`}
            style={{ cursor: getCardCursor(selectedOffersCount) }}
        >
            <div className="offer-card-main">
                <LoadCardHeader
                    loadId={offer.load.loadId}
                    showLoadId={roundState.currentPhase === 'Confirmation'}
                    checkBoxRef={checkBoxRef}
                    checked={offer.checked}
                    tag={getOfferTagStatusType(
                        roundState.currentPhase,
                        offer.accepted,
                        offer.confirmed,
                    )}
                    isPrioritized={offer.load.isPrioritized}
                    handleCardSelected={() =>
                        selectOffer(offer.load.loadId, true)
                    }
                    handleCardUnselected={() =>
                        selectOffer(offer.load.loadId, false)
                    }
                    insertedMidRound={offer.insertedMidRound}
                />

                <LoadCardBody
                    sourceRoute={
                        {
                            code: offer.load.route.source.code,
                            description: offer.load.route.source.description,
                            city: offer.load.route.source?.city,
                            federativeUnit:
                                offer.load.route.source?.federativeUnit,
                        } as IRouteDetails
                    }
                    destinationRoute={
                        {
                            code: offer.load.route.destination.code,
                            description:
                                offer.load.route.destination.description,
                            city: offer.load.route.destination?.city,
                            federativeUnit:
                                offer.load.route.destination?.federativeUnit,
                        } as IRouteDetails
                    }
                    channel={offer.load.channel}
                    collectionDate={getUTCDate(offer.load.dates.collectionDate)}
                    weight={`${offer.load.shipment.weight} ${
                        offer.load.shipment.weightUnitMeasure
                    }`}
                    price={formatCurrency(
                        acceptedRate?.chargeAmount ??
                            offer.load.shipment.chargeAmount,
                        offer.load.shipment.currency,
                        language,
                    )}
                    skids={offer.load.shipment.skids}
                    isReturnable={offer.load.isReturnable}
                />

                <LoadCardFooter
                    mainButtons={
                        <OfferCardFooterButtons
                            offer={offer}
                            isConfirmationEnabled={isConfirmationEnabled}
                        />
                    }
                    hiddenIconButtons={false}
                />
            </div>
        </div>
    );
}
